import {
  Antenna,
  Bicycle,
  Cart,
  City,
  Dishwasher,
  Garage,
  Group,
  Gym,
  Laptop,
  Packages,
  Puzzle,
  RefreshDouble,
  Shirt,
  Shop,
  Sofa,
  Stroller,
  ThreeStars,
  Tools,
  User,
  Wristwatch,
} from 'iconoir-react';

import { MessageType } from '@savgroup-front-common/types';

import messages from './messages';
import { ActivityOption } from './OnboardingActivities.types';

export enum CARRIER_CUSTOMER {
  CHRONO = 'Chrono13LocalPUDO',
  UPS = 'UpsStdFRFRHomePickup',
  IMMEDIATE_DROP_AT_STORE_DEPOSITE = 'ImmediateDropAtStoreDeposit',
  DROP_AT_STORE = 'DropAtStoreDeposit',
  PICKUP_STORE_DELIVERY = 'PickUpStoreDeliveryFRFR',
  SELLER_PROVIDED_CARRIER = 'SellerProvidedCarrier2',
}

export enum OnboardingActivityTypes {
  HIGH_TECH = 'High_tech',
  HOME_APPLIANCES = 'Electromenager',
  HANDIWORK = 'Bricolage',
  SPORT = 'Sports',
  MOBILITY = 'Mobilite',
  FURNISHING = 'Ameublement',
  BEAUTY = 'Beauté',
  MODE = 'Mode',
  JEWELRY = 'Bijoux',
  CHILDCARE = 'Puericulture',
  GAMES = 'Jouet_Loisirs_Creatifs',
  OTHER = 'Other',
  ENTERPRISE = 'Business',
  PRIVATE = 'Individual',
  BOTH_CUSTOMER = 'bothCustomer',
  ONLINE = 'online',
  STORE = 'store',
  BOTH_SALES = 'bothSales',
  STORE_RETURN = 'storeReturn',
  PICKUP_POINT = 'Chrono13LocalPUDO',
  HOME_PICKUP = 'UpsStdFRFRHomePickup',
  CLIENT = 'SellerProvidedCarrier2',
  RETRACTATION = 'Retractation',
  SAV = 'SAV',
  DELIVERY_INCIDENT = 'DeliveryIncident',
  MIN_RETURN_VOLUME = 'EnterpriseVolume_1',
  LITTLE_RETURN_VOLUME = 'EnterpriseVolume_2',
  MEDIUM_RETURN_VOLUME = 'EnterpriseVolume_3',
  HIGH_RETURN_VOLUME = 'EnterpriseVolume_4',
  MAX_RETURN_VOLUME = 'EnterpriseVolume_5',
}

export enum OnboardingCustomerVertical {
  RETAILER = 'Retailer',
  E_COMMERCE = 'E-Commerce',
  REPAIRER = 'Repairer',
  MANUFACTURER = 'Manufacturer',
  INDUSTRIAL = 'Industrial',
  WHOLESALER = 'Wholesaler',
}

export const PROPOSAL_REVEAL_SWEEP_DURATION_IN_MS = 1200;
export const PROPOSAL_REVEAL_PAUSE_DURATION_IN_MS = 800;

export const ACTIVITY_GROUP = {
  CUSTOMER_VERTICALS: 'customerVerticals',
  DOMAINS: 'domains',
  VOLUMES: 'volumes',
  CUSTOMERS: 'customers',
} as const;

export type ActivityGroup =
  (typeof ACTIVITY_GROUP)[keyof typeof ACTIVITY_GROUP];

export const ACTIVITY_GROUPS = Object.values(ACTIVITY_GROUP);

export const CUSTOMER_VERTICAL_OPTIONS: ActivityOption[] = [
  {
    value: OnboardingCustomerVertical.RETAILER,
    label: messages.retailer,
    Icon: Shop,
  },
  {
    value: OnboardingCustomerVertical.E_COMMERCE,
    label: messages.eCommercer,
    Icon: Cart,
  },
  {
    value: OnboardingCustomerVertical.REPAIRER,
    label: messages.repairer,
    Icon: Tools,
  },
  {
    value: OnboardingCustomerVertical.MANUFACTURER,
    label: messages.manufacturer,
    Icon: Packages,
  },
  {
    value: OnboardingCustomerVertical.INDUSTRIAL,
    label: messages.industrial,
    Icon: Antenna,
  },
  {
    value: OnboardingCustomerVertical.WHOLESALER,
    label: messages.wholesaler,
    Icon: Garage,
  },
];

export const DOMAIN_OPTIONS: ActivityOption[] = [
  {
    value: OnboardingActivityTypes.HIGH_TECH,
    label: messages.highTech,
    Icon: Laptop,
  },
  {
    value: OnboardingActivityTypes.HOME_APPLIANCES,
    label: messages.homeAppliances,
    Icon: Dishwasher,
  },
  {
    value: OnboardingActivityTypes.HANDIWORK,
    label: messages.handiwork,
    Icon: Tools,
  },
  { value: OnboardingActivityTypes.SPORT, label: messages.sport, Icon: Gym },
  {
    value: OnboardingActivityTypes.MOBILITY,
    label: messages.mobility,
    Icon: Bicycle,
  },
  {
    value: OnboardingActivityTypes.FURNISHING,
    label: messages.furnishing,
    Icon: Sofa,
  },
  {
    value: OnboardingActivityTypes.BEAUTY,
    label: messages.beauty,
    Icon: ThreeStars,
  },
  { value: OnboardingActivityTypes.MODE, label: messages.mode, Icon: Shirt },
  {
    value: OnboardingActivityTypes.JEWELRY,
    label: messages.jewelry,
    Icon: Wristwatch,
  },
  {
    value: OnboardingActivityTypes.CHILDCARE,
    label: messages.childcare,
    Icon: Stroller,
  },
  { value: OnboardingActivityTypes.GAMES, label: messages.games, Icon: Puzzle },
  {
    value: OnboardingActivityTypes.OTHER,
    label: messages.other,
    Icon: RefreshDouble,
  },
];

export const VOLUME_OPTIONS: ActivityOption[] = [
  {
    value: OnboardingActivityTypes.MIN_RETURN_VOLUME,
    label: messages.minReturnVolume,
  },
  {
    value: OnboardingActivityTypes.LITTLE_RETURN_VOLUME,
    label: messages.littleReturnVolume,
  },
  {
    value: OnboardingActivityTypes.MEDIUM_RETURN_VOLUME,
    label: messages.mediumReturnVolume,
  },
  {
    value: OnboardingActivityTypes.HIGH_RETURN_VOLUME,
    label: messages.highReturnVolume,
  },
  {
    value: OnboardingActivityTypes.MAX_RETURN_VOLUME,
    label: messages.maxReturnVolume,
  },
];

export const CUSTOMER_OPTIONS: ActivityOption[] = [
  {
    value: OnboardingActivityTypes.ENTERPRISE,
    label: messages.enterprises,
    Icon: City,
  },
  {
    value: OnboardingActivityTypes.PRIVATE,
    label: messages.private,
    Icon: User,
  },
  {
    value: OnboardingActivityTypes.BOTH_CUSTOMER,
    label: messages.both,
    Icon: Group,
  },
];

export const CHANGED_GROUP_MESSAGES: Record<ActivityGroup, MessageType> = {
  [ACTIVITY_GROUP.CUSTOMER_VERTICALS]: messages.changedSector,
  [ACTIVITY_GROUP.DOMAINS]: messages.changedDomains,
  [ACTIVITY_GROUP.VOLUMES]: messages.changedVolume,
  [ACTIVITY_GROUP.CUSTOMERS]: messages.changedCustomers,
};

interface ActivityQuestionDefinition {
  group: ActivityGroup;
  title: MessageType;
  description?: MessageType;
  options: ActivityOption[];
  gridClassName: string;
  dataTestId: string;
}

export const QUESTIONS: ActivityQuestionDefinition[] = [
  {
    group: ACTIVITY_GROUP.CUSTOMER_VERTICALS,
    title: messages.sectorTitle,
    options: CUSTOMER_VERTICAL_OPTIONS,
    gridClassName: 'grid-cols-2 md:grid-cols-3 min-[1560px]:grid-cols-6',
    dataTestId: 'OnboardingActivities-customerVertical',
  },
  {
    group: ACTIVITY_GROUP.DOMAINS,
    title: messages.domainsTitle,
    description: messages.domainsDescription,
    options: DOMAIN_OPTIONS,
    gridClassName: 'grid-cols-2 md:grid-cols-4 min-[1560px]:grid-cols-6',
    dataTestId: 'OnboardingActivities-domains',
  },
  {
    group: ACTIVITY_GROUP.VOLUMES,
    title: messages.volumeTitle,
    description: messages.volumeDescription,
    options: VOLUME_OPTIONS,
    gridClassName:
      'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 min-[1560px]:grid-cols-5',
    dataTestId: 'OnboardingActivities-volumes',
  },
  {
    group: ACTIVITY_GROUP.CUSTOMERS,
    title: messages.customersTitle,
    options: CUSTOMER_OPTIONS,
    gridClassName: 'grid-cols-2 md:grid-cols-3',
    dataTestId: 'OnboardingActivities-customers',
  },
];
